<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">獎勵補發</h1>
      </div>
    </div>
    <div class="page-container">
      <div class="page-notice" role="note">
        <i class="ri-information-line" aria-hidden="true"></i>
        <div>
          <p>背景自動發送沒送出去時會留在這裡。已送成功的不會列出。會員不存在、沒綁定或設定不完整也不會進清單。</p>
          <ul>
            <li><strong>Wixtar 票券</strong>：這一批已發完或數量不足。補發要另選一批新票券，活動原本的設定不會被改掉。</li>
            <li><strong>優惠券</strong>：達到總發行上限。可先把上限調高或改成不限，再重發原來的範本。</li>
            <li><strong>點數</strong>：連到點數系統失敗，例如逾時或斷線。生日禮、註冊禮、週年禮、升等禮會先隔 2 分鐘重試，最多 3 次，仍失敗才出現；行銷活動流程、分享換好康、集章、加好友、簽到是失敗當下就出現。若上次其實已加上、只是回應逾時，再補發會再加一次。</li>
            <li><strong>貼標、LINE 推播、抽獎連結</strong>：行銷活動流程或加好友送出時連線失敗。補發是把原本那一個再做一次。</li>
          </ul>
        </div>
      </div>
      <div class="page-toolbar">
        <div class="filter-group">
          <select v-model="filters.sourceType" class="form-control" @change="load(1)">
            <option value="">全部來源</option>
            <option v-for="item in sourceOptions" :key="item.value" :value="item.value">{{ item.label }}</option>
          </select>
        </div>
        <div class="filter-group filter-group--source-id">
          <input
            v-model="filters.sourceId"
            type="text"
            class="form-control"
            placeholder="活動 ID"
            @change="load(1)"
          />
        </div>
        <div class="filter-group">
          <select v-model="filters.status" class="form-control" @change="load(1)">
            <option value="pending">待補發</option>
            <option value="reissued">已補發</option>
            <option value="dismissed">不補發</option>
          </select>
        </div>
        <div v-if="filters.status === 'pending'" class="filter-group filter-group--action">
          <button type="button" class="btn btn-primary" :disabled="!selectedIds.length || submitting" @click="startReissue()">
            補發
          </button>
        </div>
        <div v-if="filters.status === 'pending'" class="filter-group filter-group--action">
          <button type="button" class="btn btn-outline-secondary" :disabled="!selectedIds.length || submitting" @click="dismissRows()">
            不補發
          </button>
        </div>
      </div>

      <div v-if="loading" class="empty-state">載入中...</div>
      <div v-else-if="!items.length" class="empty-state">目前沒有符合的紀錄</div>
      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th v-if="filters.status === 'pending'" class="check-col">
                <input type="checkbox" :checked="allSelected" @change="toggleAll" />
              </th>
              <th>時間</th>
              <th>來源</th>
              <th>活動</th>
              <th>會員</th>
              <th>項目</th>
              <th>錯誤</th>
              <th v-if="filters.status === 'pending'">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td v-if="filters.status === 'pending'">
                <input type="checkbox" :checked="selectedIds.includes(item.id)" @change="toggleOne(item.id)" />
              </td>
              <td>{{ formatTime(item.createdAt) }}</td>
              <td>{{ sourceLabel(item.sourceType) }}</td>
              <td>
                <div>{{ item.sourceName || '—' }}</div>
                <small>{{ item.sourceId }}</small>
              </td>
              <td>
                <div>{{ recipientName(item) }}</div>
                <small v-if="recipientSub(item)">{{ recipientSub(item) }}</small>
              </td>
              <td>
                <div>{{ kindLabel(item.rewardKind) }}</div>
                <small>{{ item.resourceName || item.resourceId }}</small>
              </td>
              <td>{{ item.errorMessage }}</td>
              <td v-if="filters.status === 'pending'" class="row-actions">
                <button type="button" class="btn btn-sm btn-primary" :disabled="submitting" @click="startReissue([item])">補發</button>
                <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="submitting" @click="dismissRows([item])">不補發</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        :total="pagination.totalItems"
        @change="load"
      />
    </div>

    <div v-if="ticketModal" class="modal-overlay" @click.self="ticketModal = false">
      <div class="modal-content">
        <div class="modal-header">
          <h3>選擇要補發的新票券</h3>
          <button type="button" class="btn-close" @click="ticketModal = false">×</button>
        </div>
        <div class="modal-body">
          <p class="modal-hint">已選 {{ reissueTargets.length }} 筆會套用同一張新票券。活動裡原本設定的票券不會被改掉。</p>
          <WixtarTicketMultiSelect v-model="replacementTicketIds" :tickets="wixtarTickets" :loading="wixtarTicketsLoading" />
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="ticketModal = false">取消</button>
          <button type="button" class="btn btn-primary" :disabled="submitting" @click="confirmWixtarReissue">補發</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { marketingCampaignService } from '@/api/modules/marketingCampaigns.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import ListPagination from '@/components/ListPagination.vue';
import WixtarTicketMultiSelect from '@/components/WixtarTicketMultiSelect.vue';
import { showSuccess, showError, showConfirm } from '@/utils/swal.js';

const SOURCE_OPTIONS = [
  { value: 'marketing_campaign', label: '行銷活動流程' },
  { value: 'birthday_gift', label: '生日禮' },
  { value: 'registration_gift', label: '註冊禮' },
  { value: 'anniversary_gift', label: '週年禮' },
  { value: 'tier_upgrade_gift', label: '升等禮' },
  { value: 'share_reward', label: '分享換好康' },
  { value: 'stamp_campaign', label: '集章' },
  { value: 'follow_marketing', label: '加好友行銷' },
  { value: 'checkin', label: '簽到' }
];

export default {
  name: 'RewardReissueIndex',
  components: { ListPagination, WixtarTicketMultiSelect },
  setup() {
    const route = useRoute();
    const loading = ref(false);
    const submitting = ref(false);
    const items = ref([]);
    const selectedIds = ref([]);
    const filters = reactive({
      sourceType: typeof route.query.sourceType === 'string' ? route.query.sourceType : '',
      sourceId: typeof route.query.sourceId === 'string' ? route.query.sourceId : '',
      status: 'pending'
    });
    const pagination = ref({ page: 1, totalPages: 1, totalItems: 0 });
    const ticketModal = ref(false);
    const reissueTargets = ref([]);
    const replacementTicketIds = ref([]);
    const wixtarTickets = ref([]);
    const wixtarTicketsLoading = ref(false);

    const kindLabels = {
      wixtar_ticket: 'Wixtar 票券',
      coupon: '優惠券',
      points: '點數',
      tag: '貼標',
      line_message: 'LINE 推播',
      lottery_link: '抽獎連結'
    };
    const sourceLabel = (value) => SOURCE_OPTIONS.find((item) => item.value === value)?.label || value;
    const recipientName = (item) => item.memberName || item.lineDisplayName || item.lineUserId || '—';
    const recipientSub = (item) => {
      if (item.memberName && item.lineDisplayName && item.lineDisplayName !== item.memberName) {
        return item.lineDisplayName;
      }
      if (!item.memberName && item.lineDisplayName) return 'LINE 好友';
      return '';
    };
    const kindLabel = (value) => kindLabels[value] || value;
    const allSelected = computed(() => items.value.length > 0 && items.value.every((item) => selectedIds.value.includes(item.id)));

    const formatTime = (value) => {
      if (!value) return '—';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '—';
      return date.toLocaleString('zh-TW', { hour12: false });
    };

    const errorText = (err) => err?.response?.data?.message || err?.message || '操作失敗';

    const load = async (page = 1) => {
      loading.value = true;
      selectedIds.value = [];
      try {
        const res = await marketingCampaignService.listRewardReissues({
          sourceType: filters.sourceType || undefined,
          sourceId: filters.sourceId.trim() || undefined,
          status: filters.status,
          page,
          pageSize: 20
        });
        const data = res.data?.data ?? res.data;
        items.value = data?.items || [];
        pagination.value = data?.pagination || { page: 1, totalPages: 1, totalItems: 0 };
      } catch (err) {
        console.error('載入補發清單失敗:', err);
        items.value = [];
      } finally {
        loading.value = false;
      }
    };

    const toggleOne = (id) => {
      selectedIds.value = selectedIds.value.includes(id)
        ? selectedIds.value.filter((item) => item !== id)
        : [...selectedIds.value, id];
    };

    const toggleAll = () => {
      selectedIds.value = allSelected.value ? [] : items.value.map((item) => item.id);
    };

    const ensureSameKind = (targets) => {
      const kinds = new Set(targets.map((item) => item.rewardKind));
      if (kinds.size > 1) {
        showError('請選擇同一種獎勵', '不同種類要分開補發');
        return null;
      }
      return targets[0]?.rewardKind || null;
    };

    const loadWixtarTickets = async () => {
      if (wixtarTickets.value.length) return;
      wixtarTicketsLoading.value = true;
      try {
        wixtarTickets.value = await wixtarService.listAllTicketCatalog();
      } catch (err) {
        showError('載入票券失敗', errorText(err));
      } finally {
        wixtarTicketsLoading.value = false;
      }
    };

    const startReissue = async (rows) => {
      const targets = rows || items.value.filter((item) => selectedIds.value.includes(item.id));
      if (!targets.length) return;
      const kind = ensureSameKind(targets);
      if (!kind) return;
      reissueTargets.value = targets;
      if (kind === 'wixtar_ticket') {
        replacementTicketIds.value = [];
        ticketModal.value = true;
        loadWixtarTickets();
        return;
      }
      const confirmCopy = {
        coupon: ['補發優惠券', `將用原來的優惠券範本再發 ${targets.length} 筆。活動設定不會被改掉。`],
        points: ['補發點數', `將再發同一筆點數，共 ${targets.length} 筆。若上次其實已經加上，會再加一次。`],
        tag: ['補發貼標', `將再貼同一個標籤，共 ${targets.length} 筆。已經貼上的標籤濃度可能加 1。`],
        line_message: ['補發 LINE 推播', `將再推一次原本的訊息，共 ${targets.length} 筆。`],
        lottery_link: ['補發抽獎連結', `將再推一次抽獎連結，共 ${targets.length} 筆。不會幫會員抽獎。`]
      };
      const [title, message] = confirmCopy[kind] || ['補發', `將再做原本的項目，共 ${targets.length} 筆。`];
      const confirmed = await showConfirm(title, message, '補發', '取消', 'question');
      if (!confirmed) return;
      await submitReissue(null);
    };

    const confirmWixtarReissue = async () => {
      if (replacementTicketIds.value.length !== 1) {
        showError('請選擇一張新票券', '一次補發只套用一批新的 Wixtar 票券');
        return;
      }
      await submitReissue(replacementTicketIds.value[0]);
    };

    const submitReissue = async (replacementTicketId) => {
      submitting.value = true;
      try {
        const res = await marketingCampaignService.reissueRewards({
          ids: reissueTargets.value.map((item) => item.id),
          replacementTicketId: replacementTicketId || undefined
        });
        const data = res.data?.data ?? res.data;
        ticketModal.value = false;
        const failed = data?.failed || [];
        if (failed.length) {
          await showError('有部分沒補發成功', failed.map((item) => item.errorMessage).filter(Boolean).join('；') || '請看清單上的新錯誤');
        } else {
          await showSuccess('補發完成', `已補發 ${data?.reissued || 0} 筆`);
        }
        await load(pagination.value.page || 1);
      } catch (err) {
        showError('補發失敗', errorText(err));
      } finally {
        submitting.value = false;
      }
    };

    const dismissRows = async (rows) => {
      const targets = rows || items.value.filter((item) => selectedIds.value.includes(item.id));
      if (!targets.length) return;
      const confirmed = await showConfirm('標成不補發', `這 ${targets.length} 筆會保留在清單，不會刪除，也不能再補發。`, '不補發');
      if (!confirmed) return;
      submitting.value = true;
      try {
        await marketingCampaignService.dismissRewardReissues(targets.map((item) => item.id));
        await showSuccess('已標成不補發');
        await load(pagination.value.page || 1);
      } catch (err) {
        showError('操作失敗', errorText(err));
      } finally {
        submitting.value = false;
      }
    };

    onMounted(() => load(1));

    return {
      loading,
      submitting,
      items,
      selectedIds,
      filters,
      pagination,
      sourceOptions: SOURCE_OPTIONS,
      sourceLabel,
      recipientName,
      recipientSub,
      kindLabel,
      formatTime,
      load,
      allSelected,
      toggleOne,
      toggleAll,
      startReissue,
      dismissRows,
      ticketModal,
      reissueTargets,
      replacementTicketIds,
      wixtarTickets,
      wixtarTicketsLoading,
      confirmWixtarReissue
    };
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-notice {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 0 0 16px;
  padding: 14px 16px;
  border: 1px solid #bfdbfe;
  border-left: 4px solid #2563eb;
  border-radius: 8px;
  background: #f0f7ff;
  color: #1e293b;
  font-size: 14px;
  line-height: 1.7;
}

.page-notice i {
  flex-shrink: 0;
  margin-top: 2px;
  color: #2563eb;
  font-size: 18px;
}

.page-notice p {
  margin: 0 0 8px;
}

.page-notice ul {
  margin: 0;
  padding-left: 1.2em;
}

.page-notice li + li {
  margin-top: 4px;
}

.filter-group--source-id {
  width: 320px;
}

.filter-group--action {
  width: auto;
}

.table-wrapper table {
  min-width: 1100px;
}

.empty-state {
  padding: 24px 0;
  color: #666;
}

.check-col {
  width: 36px;
}

.row-actions {
  white-space: nowrap;
}

.row-actions .btn + .btn {
  margin-left: 8px;
}

.modal-hint {
  margin: 0 0 12px;
  color: #666;
}
</style>
